import React, { useState, useEffect, useContext, useRef, useMemo, memo } from "react";
import { Card } from "react-bootstrap";
import { ChatContext } from 'context/Context';
import ChatContentBody from "../elvis-history/content/ChatContentBody";
import MessageTextArea from "../elvis-history/content/MessageTextArea";
import WindowHeader from "./WindowHeader";
import Draggable from "react-draggable";
import ReportModal from "../elvis-history/ReportModal";

// window.innerWidth/innerHeight include the scrollbar gutter, which Edge/Chromium
// reserve (~15px) and Firefox's overlay scrollbars do not, so the two browsers
// disagree by the gutter width. documentElement.clientWidth/clientHeight are the
// layout viewport with the gutter already removed and agree across browsers.
const getViewport = () => ({
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight,
});

const ChatWindow = () => {
    const { chatWindowOpen, setChatWindowOpen, setHideSidebar, setCurrentThread, currentThread, threads } = useContext(ChatContext);
    const minSize = 700;
    const [size, setSize] = useState({ right: 160, bottom: 40, width: minSize, height: minSize });
    const windowElement = useRef(null);
    // currentThread is a snapshot - the provider replaces the thread object on every
    // stream chunk, so re-resolve the live one to follow the update
    const activeThread = threads.find(x => x?.id === currentThread?.id) ?? currentThread;
    // On open reset active chat
    useEffect(() => { setCurrentThread(threads[0]); setHideSidebar(true) }, [chatWindowOpen]);

    // Responsive logic
    useEffect(() => {
        const handleResize = () => {
            const { width, height } = getViewport();
            if (width < 768) {
                // Mobile: Fullscreen
                setSize({
                    right: 0,
                    bottom: 0,
                    width: width,
                    height: height * .99,
                });
            } else if (width < 1200) {
                // Tablet: Large floating window
                setSize({
                    right: width * .1,
                    bottom: height * .05,
                    width: width * 0.85,
                    height: height * .9,
                });
            } else {
                // Desktop: Default floating window
                setSize(prev => ({
                    ...prev,
                    right: 160,
                    bottom: 40,
                    width: minSize + 100,
                    height: minSize + 25,
                }));
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Close on outside click
    useEffect(() => {
        if (!chatWindowOpen) return;
        const handleOutsideClick = ({ target }) => {
            // Don't close if the click is inside the report modal
            if (target.closest?.('.modal')) return;
            if (windowElement.current?.contains(target)) return;
            // The toggle button closes the window itself, so ignore it here
            if (target.closest?.('.chat-btn-container')) return;
            setChatWindowOpen(false);
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [chatWindowOpen]);

    // A string `bounds` is re-resolved by react-draggable on every mousemove: a
    // querySelector plus getComputedStyle and clientWidth/clientHeight reads on the
    // bound node, which for #main is the whole app root - so each move forces a
    // synchronous layout of the entire page. The window is position: fixed, so its
    // untransformed box is already known from `size` and the bounds that keep it on
    // screen are plain arithmetic, resolved once per size change instead.
    const bounds = useMemo(() => {
        const { width, height } = getViewport();
        return {
            left: -(width - size.right - size.width),
            top: -(height - size.bottom - size.height),
            right: size.right,
            bottom: size.bottom,
        };
    }, [size]);

    return (
        <>
            <Draggable handle="#handle" bounds={bounds} nodeRef={windowElement}>
                <div
                    ref={windowElement}
                    className="elvis-chat-window"
                    style={{
                        display: chatWindowOpen ? 'block' : 'none',
                        height: chatWindowOpen ? size.height : 0,
                        width: chatWindowOpen ? size.width : 0,
                        // fixed, not absolute: an absolute box resolves against the initial
                        // containing block, so it is document-relative and scrolls away from
                        // the viewport metrics it was positioned from
                        position: 'fixed',
                        bottom: (chatWindowOpen ? size.bottom : 0),
                        right: (chatWindowOpen ? size.right : 0),
                        zIndex: 1040, // Avoid clashing with report modal
                        opacity: chatWindowOpen ? 1 : 0,
                        // every property the open/close animation touches, but deliberately
                        // not transform: react-draggable writes transform inline on every
                        // mousemove, and transitioning it makes the window ease towards the
                        // cursor over 500ms instead of tracking it
                        transition: 'opacity 500ms, width 500ms, height 500ms, bottom 500ms, right 500ms',
                    }}
                >
                    <Card className='p-3 w-100 h-100'>
                        <Card.Header className="p-0 m-0">
                            <WindowHeader />
                        </Card.Header >
                        <Card.Body>
                            <div
                                className="border rounded-3 shadow-sm"
                                style={{
                                    height: size.height * .8,
                                    maxHeight: '600px',
                                    position: 'relative',
                                    overflow: 'scroll'
                                }}>
                                <ChatContentBody thread={activeThread} />
                            </div>
                            <div className="d-flex justify-content-center">
                                <MessageTextArea />
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </Draggable>
            <ReportModal />
        </>
    )
}

export default memo(ChatWindow);